import React, { useEffect, useMemo, useState } from 'react';
import BasicCli from '../../../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../../../main/constants/ApiConstants';
import IUMBInventory from '../../../../main/interfaces/IUMBInventory';
import IRow from '../../../../main/interfaces/IRow';
import IUMB from '../../../../main/interfaces/IUMB';
import IUser from '../../../../main/interfaces/IUser';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axiosInstance from '../../../../main/initializers/axios';
import { useTranslation } from 'react-i18next';
import useGetUser from '../../../../main/hooks/useGetUser';
import '../../umbsManagement/UmbsManagement.css';
import './UserUmbrellaInventory.css';
import getUmbrellaPriceSummary from '../../getUmbrellaPriceSummary';

const UserUmbrellaInventory = () => {
  const { t } = useTranslation();
  const currentUser = useGetUser();
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [umbInventory, setUmbInventory] = useState<IUMBInventory[]>([]);
  const [dateSelected, setDateSelected] = useState<string>(new Date().toISOString());
  const [closed, setClosed] = useState<boolean>(false);
  const [rows, setRows] = useState<IRow[]>([]);
  const [umbrellas, setUmbrellas] = useState<IUMB[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<number>(0);
  const [selectedUmbCode, setSelectedUmbCode] = useState<string>('');

  const userDisplayName = useMemo(() => {
    const selectedUser = users.find((user) => user.id === selectedUserId) ?? (currentUser?.id === selectedUserId ? currentUser : undefined);
    const fullName = `${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`.trim();
    return fullName || selectedUser?.userName || selectedUser?.email || t('userUmbInventory.user');
  }, [currentUser, selectedUserId, t, users]);

  const rowFilterLabel = t('userUmbInventory.row');
  const codeFilterLabel = t('userUmbInventory.code');

  const getUsers = () => {
    axiosInstance
      .get(ApiConstants.User.users)
      .then((response) => {
        const loadedUsers = (response.data ?? []) as IUser[];
        if (currentUser && !loadedUsers.some((user) => user.id === currentUser.id)) {
          loadedUsers.unshift(currentUser);
        }
        setUsers(loadedUsers);
      })
      .catch((error) => {
        if (currentUser) {
          setUsers([currentUser]);
        }
        console.error(t('errors.fetchUsers'), error);
      });
  };

  const getRows = () => {
    axiosInstance
      .get(`${ApiConstants.row.list}?rowType=1`)
      .then((response) => setRows(response.data ?? []))
      .catch((error) => {
        console.error(t('userUmbInventory.messages.loadError'), error);
      });
  };

  const getUmbrellas = () => {
    axiosInstance
      .get(ApiConstants.umb.list)
      .then((response) => setUmbrellas(response.data ?? []))
      .catch((error) => {
        console.error(t('userUmbInventory.messages.loadError'), error);
      });
  };

  const getUMBInventory = (userId: number, date: Date, isClosed: boolean) => {
    if (!userId) {
      setUmbInventory([]);
      return;
    }

    const param = {
      userId,
      date,
      closed: isClosed,
    };

    axiosInstance
      .post(ApiConstants.umb.umbInvnetory, param)
      .then((response) => {
        const items = response.data ?? [];
        setUmbInventory(items);
      })
      .catch((error) => {
        console.error(t('userUmbInventory.messages.loadError'), error);
      });
  };

  useEffect(() => {
    getUsers();
    getRows();
    getUmbrellas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser?.id || selectedUserId !== 0) return;
    setSelectedUserId(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    getUMBInventory(selectedUserId, new Date(dateSelected), closed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const handleUserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(Number(event.target.value));
    setSelectedRowId(0);
    setSelectedUmbCode('');
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.validity.valid) return;
    const dt = event.target.value + ':00Z';
    setDateSelected(dt);
    getUMBInventory(selectedUserId, new Date(dt), closed);
  };

  const handleClosedInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = event.target.checked;
    setClosed(isClosed);
    getUMBInventory(selectedUserId, new Date(dateSelected), isClosed);
  };

  const formatDate = (val: Date | undefined): string => {
    if (val) {
      const date = new Date(val);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return '';
  };

  const formatPrice = (val: number | undefined): string => Number(val || 0).toFixed(2);

  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string | undefined, length: number) => {
    if (text == null || text === '') return '';
    return text.substring(0, length);
  };

  const getUmbrella = (item: IUMBInventory): IUMB | undefined => {
    return item.umb || umbrellas.find((umbrella) => umbrella.id === item.caderId);
  };

  const getRowId = (item: IUMBInventory): number => {
    const umbrella = getUmbrella(item);
    return umbrella?.rowId ?? item.umb?.row?.id ?? 0;
  };

  const getRowLabel = (item: IUMBInventory): string => {
    const umbrella = getUmbrella(item);
    const rowId = umbrella?.rowId ?? item.umb?.row?.id;
    const row = rows.find((x) => x.id === rowId);
    return item.umb?.row?.code || row?.code || '-';
  };

  const getUmbCode = (item: IUMBInventory): string => {
    const umbrella = getUmbrella(item);
    return umbrella?.code || String(item.caderId || '-');
  };

  const codeOptions = useMemo(() => {
    const source = selectedRowId === 0 ? umbrellas : umbrellas.filter((umbrella) => umbrella.rowId === selectedRowId);

    return Array.from(new Set(source.map((umbrella) => umbrella.code).filter((code) => code && code !== '-'))).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [umbrellas, selectedRowId]);

  useEffect(() => {
    if (selectedUmbCode && !codeOptions.includes(selectedUmbCode)) {
      setSelectedUmbCode('');
    }
  }, [codeOptions, selectedUmbCode]);

  const filteredInventory = useMemo(() => {
    let items = [...umbInventory];

    if (selectedRowId !== 0) {
      items = items.filter((item) => getRowId(item) === selectedRowId);
    }

    if (selectedUmbCode) {
      items = items.filter((item) => getUmbCode(item) === selectedUmbCode);
    }

    return items.sort((a, b) => {
      const rowCompare = getRowLabel(a).localeCompare(getRowLabel(b), undefined, { numeric: true });
      if (rowCompare !== 0) return rowCompare;
      return getUmbCode(a).localeCompare(getUmbCode(b), undefined, { numeric: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [umbInventory, selectedRowId, selectedUmbCode, rows, umbrellas]);

  const filteredTotal = useMemo(() => {
    return filteredInventory.reduce((sum, item) => sum + (item.price || 0), 0);
  }, [filteredInventory]);

  const priceSummary = useMemo(() => getUmbrellaPriceSummary(filteredInventory), [filteredInventory]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="sq">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t('userUmbInventory.report.windowTitle')}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #000; }
          .print-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          .print-header h1 { font-size: 24px; margin: 0; color: #000; }
          .print-header .info { margin-top: 10px; font-size: 14px; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #f2f2f2; padding: 12px; border: 1px solid #000; font-weight: bold; text-align: left; }
          td { padding: 10px; border: 1px solid #000; }
          .total-section { margin-top: 30px; text-align: right; font-weight: bold; font-size: 18px; border-top: 2px solid #000; padding-top: 15px; }
          .price-breakdown { margin-top: 8px; font-size: 15px; font-weight: normal; line-height: 1.6; }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>${t('userUmbInventory.report.title')}</h1>
          <div class="info">
            <strong>${t('userUmbInventory.report.employee')}:</strong> ${userDisplayName}<br>
            <strong>${t('userUmbInventory.report.date')}:</strong> ${formatDateForDisplay(dateSelected)}<br>
            <strong>${t('userUmbInventory.report.status')}:</strong> ${closed ? t('userUmbInventory.report.closed') : t('userUmbInventory.report.open')}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>${t('userUmbInventory.table.code')}</th>
              <th>${t('userUmbInventory.table.row')}</th>
              <th>${t('userUmbInventory.table.open')}</th>
              <th>${t('userUmbInventory.table.close')}</th>
              <th>${t('userUmbInventory.table.note')}</th>
              <th style="text-align: right;">${t('userUmbInventory.table.price')}</th>
            </tr>
          </thead>
          <tbody>
            ${filteredInventory
              .map(
                (item) => `
                <tr>
                  <td>${getUmbCode(item)}</td>
                  <td>${getRowLabel(item)}</td>
                  <td>${formatDate(item.openTime)}</td>
                  <td>${formatDate(item.closeTime)}</td>
                  <td>${item.switch || ''}</td>
                  <td style="text-align: right;">${formatPrice(item.price)}</td>
                </tr>
              `,
              )
              .join('')}
          </tbody>
        </table>
        <div class="total-section">
          ${t('userUmbInventory.report.total')} ${formatPrice(filteredTotal)}
          <div class="price-breakdown">
            ${priceSummary
              .map(
                (item) =>
                  `<div>${item.count} x ${formatPrice(item.price)} = ${formatPrice(item.count * item.price)}</div>`,
              )
              .join('')}
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const exportToPDF = () => {
    alert(t('userUmbInventory.messages.pdfPlaceholder'));
  };

  return (
    <BasicCli>
      <div className="umbmPage">
        <div className="ui-panel umbMToolbar">
          <div className="umbMFilters userUmbFilters">
            <select
              className="ui-control ui-select userUmbUserSelect"
              value={selectedUserId}
              onChange={handleUserChange}
              aria-label={t('invoice.optionSelectEmployee')}
            >
              <option value={0}>{t('invoice.optionSelectEmployee')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userName || user.email}
                </option>
              ))}
            </select>

            <div className="userUmbFilterGroup">
              <select className="ui-control ui-select userUmbFilterSelect" value={selectedRowId} onChange={(event) => setSelectedRowId(Number(event.target.value))}>
                <option value={0}>{rowFilterLabel}</option>
                {rows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.code}
                  </option>
                ))}
              </select>

              <select className="ui-control ui-select userUmbFilterSelect" value={selectedUmbCode} onChange={(event) => setSelectedUmbCode(event.target.value)}>
                <option value="">{codeFilterLabel}</option>
                {codeOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>

            <div className="userUmbDate">
              <input
                type="datetime-local"
                className="ui-control ui-input"
                value={dateSelected.toString().substring(0, 16)}
                onChange={handleDateChange}
              />
            </div>

            <label className="umbMCheck userUmbClosed">
              <input type="checkbox" className="ui-check" checked={closed} onChange={handleClosedInput} />
              <span>{t('userUmbInventory.closed')}</span>
            </label>

            <div className="umbMActions">
              <button className="ui-control ui-btn ui-btn--primary" onClick={handlePrint}>
                <PrintIcon fontSize="small" />
                {t('userUmbInventory.print')}
              </button>

              <button className="ui-control ui-btn ui-btn--danger" onClick={exportToPDF}>
                <PictureAsPdfIcon fontSize="small" />
                {t('userUmbInventory.pdf')}
              </button>
            </div>
          </div>
        </div>

        <div className="umbMGrid">
          <div className="bcCard umbMTableCard">
            <div className="umbMTableScroll">
              <table className="bcTable">
                <thead>
                  <tr>
                    <th>{t('userUmbInventory.table.code')}</th>
                    <th>{t('userUmbInventory.table.row')}</th>
                    <th>{t('userUmbInventory.table.open')}</th>
                    <th>{t('userUmbInventory.table.close')}</th>
                    <th>{t('userUmbInventory.table.note')}</th>
                    <th style={{ textAlign: 'right' }}>{t('userUmbInventory.table.price')}</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredInventory.length > 0 ? (
                    filteredInventory.map((item) => (
                      <tr key={item.id} className={item.switch != null ? 'umbMRowWarn' : undefined}>
                        <td style={{ fontWeight: 600 }}>{getUmbCode(item)}</td>
                        <td>{getRowLabel(item)}</td>
                        <td>{formatDate(item.openTime)}</td>
                        <td>{formatDate(item.closeTime)}</td>
                        <td title={item.switch}>{truncateText(item.switch, 30)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{formatPrice(item.price)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="umbMEmpty">
                        <h4>{t('userUmbInventory.empty.title')}</h4>
                        <p>{selectedUserId === 0 ? t('invoice.optionSelectEmployee') : t('userUmbInventory.empty.message')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bcCard umbMSide">
            <div className="umbMSideTop">
              <div>
                <h4 className="umbMTitle">{userDisplayName}</h4>
                <small className="bcMuted">{formatDateForDisplay(dateSelected)}</small>
              </div>
            </div>

            <hr className="umbMDivider" />

            <div className="umbMSummary">
              <div className="umbMRow">
                <span className="bcMuted">{t('userUmbInventory.summary.totalItems')}</span>
                <strong>{filteredInventory.length}</strong>
              </div>

              <div className="umbMRow">
                <span style={{ fontWeight: 700 }}>{t('userUmbInventory.summary.total')}</span>
                <div className="umbMTotal">{formatPrice(filteredTotal)}</div>
              </div>

              <div className="umbMPriceSummary">
                {priceSummary.map((item) => (
                  <div key={item.price}>
                    {item.count} x {formatPrice(item.price)} = {formatPrice(item.count * item.price)}
                  </div>
                ))}
              </div>
            </div>

            <hr className="umbMDivider" />
            <div className="umbMStatus">
              <small className="bcMuted">
                {t('userUmbInventory.summary.status')}{' '}
                <span style={{ color: closed ? 'var(--success)' : 'var(--warning)', fontWeight: 800 }}>
                  {closed ? t('userUmbInventory.summary.closed') : t('userUmbInventory.summary.open')}
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>
    </BasicCli>
  );
};

export default UserUmbrellaInventory;
